import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import { useCallback, useRef, useMemo } from "react";
import { useApolloClient, useMutation, useLazyQuery } from "@apollo/client";
import { retrieveCartId } from "@magento/peregrine/lib/store/actions/cart";
import { useUserContext } from "@magento/peregrine/lib/context/user";
import { useCartContext } from "../../context/cart";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { clearCartDataFromCache } from "@magento/peregrine/lib/Apollo/clearCartDataFromCache";
import { clearCustomerDataFromCache } from "@magento/peregrine/lib/Apollo/clearCustomerDataFromCache";
import DEFAULT_OPERATIONS from "./signIn.gql";
import UIkit from "uikit";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import CheckoutPageOperations from "@overrides_venia/venia-ui/components/CheckoutPage/checkoutPage.gql";
import { format } from "date-fns";
export var useSignIn = _ref => {
  var {
    setDefaultUsername,
    showCreateAccount,
    showForgotPassword
  } = _ref;
  var {
    mutations: {
      calculateLSCartMutation,
      setTakeawayDataOnCart
    },
    queries: {
      getCheckoutDetailsQuery
    }
  } = CheckoutPageOperations;
  var {
    createCartMutation,
    getCustomerQuery,
    mergeCartsMutation,
    signInMutation
  } = DEFAULT_OPERATIONS;
  var apolloClient = useApolloClient();
  var [{
    cartId
  }, {
    createCart,
    removeCart,
    updateCartData
  }] = useCartContext();
  var [{
    isGettingDetails,
    getDetailsError,
    isSigningIn
  }, {
    getUserDetails,
    setToken,
    submitIsSigningIn
  }] = useUserContext();
  var [{
    tableData,
    pickupTime
  }, {
    submitCheckoutStepCompleted
  }] = useCheckoutContext();
  var [signIn, {
    error: signInError
  }] = useMutation(signInMutation, {
    fetchPolicy: "no-cache"
  });
  var [setDataOnCart] = useMutation(setTakeawayDataOnCart);
  var [fetchCartId] = useMutation(createCartMutation);
  var [mergeCarts] = useMutation(mergeCartsMutation);
  var [calculateLSCart] = useMutation(calculateLSCartMutation);
  var [getCheckoutDetails] = useLazyQuery(getCheckoutDetailsQuery, {
    fetchPolicy: "no-cache",
    skip: !cartId,
    onError: error => {
      console.log(error);
    }
  });
  var fetchUserDetails = useAwaitQuery(getCustomerQuery);
  var formApiRef = useRef(null);
  var setFormApi = useCallback(api => formApiRef.current = api, []);
  var handlePostSignIn = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (token) {
      var sourceCartId = cartId;
      yield setToken(token);

      // Clear all cart/customer data from cache and redux.
      yield clearCartDataFromCache(apolloClient);
      yield clearCustomerDataFromCache(apolloClient);
      yield removeCart();
      getUserDetails({
        fetchUserDetails
      });
      submitCheckoutStepCompleted({
        pickupTime: false,
        shippingAddress: false,
        payment: false,
        review: false,
        tableData: false,
        shippingMethod: false,
        shippingDataSet: false
      });
      // Create and get the customer's cart id.
      yield createCart({
        fetchCartId
      });
      var destinationCartId = yield retrieveCartId();
      // Merge the guest cart into the customer cart.
      yield mergeCarts({
        variables: {
          destinationCartId,
          sourceCartId: sourceCartId
        }
      });
      if (tableData.store) {
        var _tableData$store;
        var selectedPickupDateTime = pickupTime !== null && pickupTime !== void 0 && pickupTime.date && pickupTime !== null && pickupTime !== void 0 && pickupTime.hour && typeof (pickupTime === null || pickupTime === void 0 ? void 0 : pickupTime.hour) === "string" ? pickupTime.date + "T" + pickupTime.hour + ":00" : null;
        var currentISOTimestampAdjString = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
        yield setDataOnCart({
          variables: {
            cart_id: destinationCartId,
            pickup_date: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
            pickup_time: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
            ls_store_id: (_tableData$store = tableData.store) === null || _tableData$store === void 0 ? void 0 : _tableData$store.id,
            ls_covers_quantity: 0,
            ls_sales_type: tableData.sales_type,
            ls_table_number: tableData === null || tableData === void 0 ? void 0 : tableData.table
          }
        });
        yield calculateLSCart({
          variables: {
            cartId: destinationCartId
          }
        });
      }
      yield getCheckoutDetails({
        variables: {
          cartId: destinationCartId
        },
        onCompleted: function () {
          var _ref3 = _asyncToGenerator(function* (data) {
            yield updateCartData(data.cart);
            submitIsSigningIn(false);
          });
          return function onCompleted(_x2) {
            return _ref3.apply(this, arguments);
          };
        }()
      });
      try {
        UIkit.offcanvas("#offcanvas-login-dx").hide();
      } catch (error) {
        console.error(error);
      }
    });
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [cartId, setToken, apolloClient, removeCart, getUserDetails, fetchUserDetails, submitCheckoutStepCompleted, createCart, fetchCartId, mergeCarts, tableData, pickupTime, setDataOnCart, calculateLSCart, getCheckoutDetails, updateCartData, submitIsSigningIn]);
  var handleSubmit = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator(function* (_ref4) {
      var {
        email,
        password
      } = _ref4;
      submitIsSigningIn(true);
      try {
        // Sign in and set the token.
        var signInResponse = yield signIn({
          variables: {
            email,
            password
          }
        });
        var token = signInResponse.data.generateCustomerToken.token;
        yield handlePostSignIn(token);
      } catch (error) {
        console.error(error);
        submitIsSigningIn(false);
      }
    });
    return function (_x3) {
      return _ref5.apply(this, arguments);
    };
  }(), [signIn, handlePostSignIn, submitIsSigningIn]);
  var handleForgotPassword = useCallback(() => {
    var {
      current: formApi
    } = formApiRef;
    if (formApi) {
      setDefaultUsername(formApi.getValue("email"));
    }
    showForgotPassword();
  }, [setDefaultUsername, showForgotPassword]);
  var handleCreateAccount = useCallback(() => {
    var {
      current: formApi
    } = formApiRef;
    if (formApi) {
      setDefaultUsername(formApi.getValue("email"));
    }
    showCreateAccount();
  }, [setDefaultUsername, showCreateAccount]);
  var errors = useMemo(() => new Map([["getUserDetailsQuery", getDetailsError], ["signInMutation", signInError]]), [getDetailsError, signInError]);
  return {
    errors,
    handleCreateAccount,
    handleForgotPassword,
    handleSubmit,
    handlePostSignIn,
    isBusy: isGettingDetails || isSigningIn,
    setFormApi
  };
};